/**
 * Zustand store for Mermaid diagram editor state
 * Manages nodes, edges, history (undo/redo), and diagram metadata
 */

import { addEdge, applyEdgeChanges, applyNodeChanges } from "@xyflow/react"
import { create } from "zustand"

import type { MermaidDirection, MermaidEdge, MermaidNode, ParsedMermaid } from "../utils"
import type { OnConnect, OnEdgesChange, OnNodesChange } from "@xyflow/react"

import { generateMermaidCode, generateNewNodeId, parseMermaidCode } from "../utils"
import { createInitialState, type HistoryEntry, saveToHistory } from "./mermaid-editor-helpers"

type NodePositionsById = Record<string, { x: number; y: number }>

export type MermaidEditorState = {
  diagramId: string | null
  title: string
  direction: MermaidDirection
  nodes: MermaidNode[]
  edges: MermaidEdge[]
  code: string
  subgraphs: ParsedMermaid["subgraphs"]

  history: HistoryEntry[]
  historyIndex: number
  isDirty: boolean
  isValid: boolean
  parseError: string | null

  syncSource: "visual" | "code" | null

  selectedNodeIds: string[]
  setSelectedNodeIds: (ids: string[]) => void
  getSelectedNodes: () => MermaidNode[]
  deleteSelectedNodes: () => void

  onNodesChange: OnNodesChange
  onEdgesChange: OnEdgesChange
  onConnect: OnConnect
  setNodes: (nodes: MermaidNode[]) => void
  setEdges: (edges: MermaidEdge[]) => void
  setCode: (code: string) => void
  setDirection: (direction: MermaidDirection) => void
  setTitle: (title: string) => void
  applyNodeLayout: (positions: NodePositionsById) => void

  addNode: (
    shape?: MermaidNode["data"]["shape"],
    label?: string,
    position?: { x: number; y: number }
  ) => void
  updateNodeData: (nodeId: string, data: Partial<MermaidNode["data"]>) => void
  deleteNode: (nodeId: string) => void

  loadDiagram: (id: string | null, title: string, code: string) => void
  loadFromCode: (code: string) => void
  syncCodeFromVisual: () => void
  syncVisualFromCode: () => void
  resetDiagram: () => void

  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean

  markClean: () => void
}

export const useMermaidEditorStore = create<MermaidEditorState>((set, get) => ({
  ...createInitialState(),

  setSelectedNodeIds: (ids) => {
    set({ selectedNodeIds: ids })
  },

  getSelectedNodes: () => {
    const state = get()
    return state.nodes.filter((node) => state.selectedNodeIds.includes(node.id))
  },

  deleteSelectedNodes: () => {
    const state = get()
    if (state.selectedNodeIds.length === 0) return

    const nodesToDelete = new Set(state.selectedNodeIds)
    const newNodes = state.nodes.filter((node) => !nodesToDelete.has(node.id))
    const newEdges = state.edges.filter(
      (edge) => !nodesToDelete.has(edge.source) && !nodesToDelete.has(edge.target)
    )
    const newCode = generateMermaidCode(newNodes, newEdges, state.direction, state.subgraphs)

    set({
      nodes: newNodes,
      edges: newEdges,
      code: newCode,
      selectedNodeIds: [],
      syncSource: "visual",
      ...saveToHistory(state, { nodes: newNodes, edges: newEdges, code: newCode }),
    })
  },

  onNodesChange: (changes) => {
    const state = get()
    const newNodes = applyNodeChanges(changes, state.nodes) as MermaidNode[]
    const isSignificantChange = changes.some(
      (change) => change.type === "add" || change.type === "remove"
    )

    const newCode = generateMermaidCode(newNodes, state.edges, state.direction, state.subgraphs)

    if (isSignificantChange) {
      set({
        nodes: newNodes,
        code: newCode,
        syncSource: "visual",
        ...saveToHistory(state, { nodes: newNodes, edges: state.edges, code: newCode }),
      })
    } else {
      set({ nodes: newNodes, code: newCode, syncSource: "visual", isDirty: true })
    }
  },

  onEdgesChange: (changes) => {
    const state = get()
    const newEdges = applyEdgeChanges(changes, state.edges) as MermaidEdge[]
    const isSignificantChange = changes.some(
      (change) => change.type === "add" || change.type === "remove"
    )

    const newCode = generateMermaidCode(state.nodes, newEdges, state.direction, state.subgraphs)

    if (isSignificantChange) {
      set({
        edges: newEdges,
        code: newCode,
        syncSource: "visual",
        ...saveToHistory(state, { nodes: state.nodes, edges: newEdges, code: newCode }),
      })
    } else {
      set({ edges: newEdges, code: newCode, syncSource: "visual", isDirty: true })
    }
  },

  onConnect: (connection) => {
    const state = get()
    const newEdge: MermaidEdge = {
      ...connection,
      id: `${connection.source}-${connection.target}-${Date.now()}`,
      type: "mermaidEdge",
      data: { style: "solid", sourceArrow: "none", targetArrow: "arrow" },
    } as MermaidEdge
    const newEdges = addEdge(newEdge, state.edges) as MermaidEdge[]
    const newCode = generateMermaidCode(state.nodes, newEdges, state.direction, state.subgraphs)

    set({
      edges: newEdges,
      code: newCode,
      syncSource: "visual",
      ...saveToHistory(state, { nodes: state.nodes, edges: newEdges, code: newCode }),
    })
  },

  setNodes: (nodes) => {
    const state = get()
    const newCode = generateMermaidCode(nodes, state.edges, state.direction, state.subgraphs)
    set({
      nodes,
      code: newCode,
      syncSource: "visual",
      ...saveToHistory(state, { nodes, edges: state.edges, code: newCode }),
    })
  },

  setEdges: (edges) => {
    const state = get()
    const newCode = generateMermaidCode(state.nodes, edges, state.direction, state.subgraphs)
    set({
      edges,
      code: newCode,
      syncSource: "visual",
      ...saveToHistory(state, { nodes: state.nodes, edges, code: newCode }),
    })
  },

  setCode: (code) => {
    const state = get()
    set({
      code,
      syncSource: "code",
      isDirty: true,
    })

    try {
      const parsed = parseMermaidCode(code)
      set({
        nodes: parsed.nodes,
        edges: parsed.edges,
        direction: parsed.direction,
        subgraphs: parsed.subgraphs,
        isValid: true,
        parseError: null,
        ...saveToHistory(state, { nodes: parsed.nodes, edges: parsed.edges, code }),
      })
    } catch (error) {
      set({
        isValid: false,
        parseError: error instanceof Error ? error.message : "Invalid Mermaid syntax",
      })
    }
  },

  setDirection: (direction) => {
    const state = get()
    const newCode = generateMermaidCode(state.nodes, state.edges, direction, state.subgraphs)
    set({
      direction,
      code: newCode,
      syncSource: "visual",
      ...saveToHistory(state, { nodes: state.nodes, edges: state.edges, code: newCode }),
    })
  },

  setTitle: (title) => {
    set({ title, isDirty: true })
  },

  applyNodeLayout: (positions) => {
    const state = get()
    const positionEntries = Object.entries(positions)
    if (positionEntries.length === 0 || state.nodes.length === 0) return

    let didChange = false
    const newNodes = state.nodes.map((node) => {
      if (!(node.id in positions)) return node
      const nextPosition = positions[node.id]

      if (node.position.x === nextPosition.x && node.position.y === nextPosition.y) return node

      didChange = true
      return { ...node, position: nextPosition }
    })

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!didChange) return

    const newHistory = state.history.slice()
    const currentEntry = newHistory[state.historyIndex]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (currentEntry) {
      newHistory[state.historyIndex] = { ...currentEntry, nodes: structuredClone(newNodes) }
    }

    set({ nodes: newNodes, history: newHistory })
  },

  addNode: (shape = "rectangle", label, position) => {
    const state = get()
    const newId = generateNewNodeId(state.nodes)
    const newLabel = label || `Node ${newId}`

    const newNode: MermaidNode = {
      id: newId,
      type: "mermaid",
      position: position || { x: 100 + state.nodes.length * 50, y: 100 + state.nodes.length * 30 },
      data: { label: newLabel, shape },
    }

    const newNodes = [...state.nodes, newNode]
    const newCode = generateMermaidCode(newNodes, state.edges, state.direction, state.subgraphs)

    set({
      nodes: newNodes,
      code: newCode,
      syncSource: "visual",
      ...saveToHistory(state, { nodes: newNodes, edges: state.edges, code: newCode }),
    })
  },

  updateNodeData: (nodeId, data) => {
    const state = get()
    const newNodes = state.nodes.map((node) =>
      node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
    )
    const newCode = generateMermaidCode(newNodes, state.edges, state.direction, state.subgraphs)

    set({
      nodes: newNodes,
      code: newCode,
      syncSource: "visual",
      isDirty: true,
    })
  },

  deleteNode: (nodeId) => {
    const state = get()
    const newNodes = state.nodes.filter((node) => node.id !== nodeId)
    const newEdges = state.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
    const newCode = generateMermaidCode(newNodes, newEdges, state.direction, state.subgraphs)

    set({
      nodes: newNodes,
      edges: newEdges,
      code: newCode,
      syncSource: "visual",
      ...saveToHistory(state, { nodes: newNodes, edges: newEdges, code: newCode }),
    })
  },

  loadDiagram: (id, title, code) => {
    try {
      const parsed = parseMermaidCode(code)
      set({
        diagramId: id,
        title,
        direction: parsed.direction,
        nodes: parsed.nodes,
        edges: parsed.edges,
        code,
        subgraphs: parsed.subgraphs,
        history: [{ nodes: parsed.nodes, edges: parsed.edges, code }],
        historyIndex: 0,
        isDirty: false,
        isValid: true,
        parseError: null,
        syncSource: null,
        selectedNodeIds: [],
      })
    } catch (error) {
      set({
        diagramId: id,
        title,
        code,
        nodes: [],
        edges: [],
        isValid: false,
        parseError: error instanceof Error ? error.message : "Invalid Mermaid syntax",
        isDirty: false,
        syncSource: null,
        selectedNodeIds: [],
      })
    }
  },

  loadFromCode: (code) => {
    const state = get()
    try {
      const parsed = parseMermaidCode(code)
      set({
        direction: parsed.direction,
        nodes: parsed.nodes,
        edges: parsed.edges,
        code,
        subgraphs: parsed.subgraphs,
        isValid: true,
        parseError: null,
        syncSource: "code",
        ...saveToHistory(state, { nodes: parsed.nodes, edges: parsed.edges, code }),
      })
    } catch (error) {
      set({
        code,
        isValid: false,
        parseError: error instanceof Error ? error.message : "Invalid Mermaid syntax",
        syncSource: "code",
      })
    }
  },

  syncCodeFromVisual: () => {
    const state = get()
    const newCode = generateMermaidCode(state.nodes, state.edges, state.direction, state.subgraphs)
    set({ code: newCode, syncSource: "visual" })
  },

  syncVisualFromCode: () => {
    const state = get()
    try {
      const parsed = parseMermaidCode(state.code)
      const existingPositionsById: NodePositionsById = Object.fromEntries(
        state.nodes.map((node) => [node.id, node.position])
      )

      const newNodes = parsed.nodes.map((node) => {
        if (!(node.id in existingPositionsById)) return node
        const existingPosition = existingPositionsById[node.id]

        if (node.position.x === existingPosition.x && node.position.y === existingPosition.y)
          return node

        return { ...node, position: existingPosition }
      })
      set({
        direction: parsed.direction,
        nodes: newNodes,
        edges: parsed.edges,
        subgraphs: parsed.subgraphs,
        isValid: true,
        parseError: null,
        syncSource: "code",
      })
    } catch (error) {
      set({
        isValid: false,
        parseError: error instanceof Error ? error.message : "Invalid Mermaid syntax",
      })
    }
  },

  resetDiagram: () => {
    set(createInitialState())
  },

  undo: () => {
    const state = get()
    if (state.historyIndex > 0) {
      const newIndex = state.historyIndex - 1
      const entry = state.history[newIndex]
      set({
        nodes: structuredClone(entry.nodes),
        edges: structuredClone(entry.edges),
        code: entry.code,
        historyIndex: newIndex,
        syncSource: null,
      })
    }
  },

  redo: () => {
    const state = get()
    if (state.historyIndex < state.history.length - 1) {
      const newIndex = state.historyIndex + 1
      const entry = state.history[newIndex]
      set({
        nodes: structuredClone(entry.nodes),
        edges: structuredClone(entry.edges),
        code: entry.code,
        historyIndex: newIndex,
        syncSource: null,
      })
    }
  },

  canUndo: () => get().historyIndex > 0,

  canRedo: () => {
    const state = get()
    return state.historyIndex < state.history.length - 1
  },

  markClean: () => {
    set({ isDirty: false })
  },
}))
